<template>
  <div class="popup-form popup" @dragover="dragover" @dragleave="dragleave">
    <div class="pdf-section">
      <div v-if="!loadingPdf && sourcePdf" class="pdf-viewer">
        <div
          v-if="documentTabs.length > 1"
          class="application-document-tabs"
          role="tablist"
          aria-label="Dokumenty k žiadosti"
        >
          <button
            v-for="document in documentTabs"
            :key="document.id"
            type="button"
            role="tab"
            class="application-document-tab"
            :class="{ active: document.id === activeDocumentId }"
            :aria-selected="document.id === activeDocumentId"
            :title="document.label"
            @click="selectDocumentTab(document)"
          >
            {{ document.label }}
          </button>
        </div>
        <div v-if="aiTemplateAvailable" class="ai-generation-bar" :class="{ completed: aiGenerated }">
          <div class="ai-generation-copy">
            <span class="ai-sparkle" aria-hidden="true">✦</span>
            <div>
              <strong>{{ aiGenerated ? "AI návrh je pripravený" : "Vyplniť žiadosť pomocou AI" }}</strong>
              <span>
                {{ aiGenerated
                  ? `${aiFilledCount} bezpečne doplnených polí, dokument ostáva editovateľný.`
                  : "GPT použije údaje zo žiadosti, klientov, príjmov a nehnuteľností." }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="ai-generate-button"
            :disabled="aiGenerating"
            @click="generateWithAi"
          >
            <span v-if="aiGenerating" class="ai-button-spinner" aria-hidden="true"></span>
            <span v-else aria-hidden="true">✦</span>
            {{ aiGenerating ? "Vypĺňam..." : aiGenerated ? "Vygenerovať znova" : "Generovať žiadosť" }}
          </button>
        </div>
        <div class="pdf-canvas">
          <application-pdf-editor
            v-if="aiGeneratedPdfBlob"
            v-show="activeDocumentId === 'ai-generated-application'"
            :key="aiEditorVersion"
            ref="aiPdfEditor"
            :pdf-blob="aiGeneratedPdfBlob"
            @ready="aiEditorReady = true"
            @dirty="aiEditorDirty = true"
            @error="onAiEditorError"
          />
          <iframe
            v-show="activeDocumentId !== 'ai-generated-application'"
            id="iframepdf"
            :key="activeDocumentId"
            :src="sourcePdf"
            title="Náhľad a editácia žiadosti"
          />
          <transition name="ai-overlay">
            <div v-if="aiGenerating" class="ai-loading-overlay" role="status" aria-live="polite">
              <div class="ai-orbit" aria-hidden="true">
                <span class="ai-orbit-ring"></span>
                <span class="ai-orbit-sparkle">✦</span>
              </div>
              <strong>GPT vypĺňa žiadosť</strong>
              <span>{{ aiLoadingMessage }}</span>
              <div class="ai-loading-dots" aria-hidden="true"><i></i><i></i><i></i></div>
            </div>
          </transition>
        </div>
      </div>
      <div class="no-pdf-link" v-else-if="!loadingPdf && !sourcePdf">
        <img src="@/images/no-generated-pdf-image.svg" alt="">
        <div>
          v prípade tejto banky je potrebné žiadosť <br>
          zadať <a :href="getLinkToApplication(payload.application?.bank?.code_name)" target="_blank" class="bold">online TU</a>,
          vygenerovanú žiadosť <br>online nezabudnite nahrať hore vpravo
        </div>
      </div>
      <div class="d-flex justify-content-center align-items-center h-100 w-100" v-else>
        <v-progress-circular
          class="loading-spinner"
          indeterminate
        ></v-progress-circular>
      </div>
    </div>
    <div class="infomation-section">
      <div class="content-form-popup">
        <div class="header-text">
          <div class="back-button-wrapper pointer" @click="closePopup">
            <img
              class="nav-link-icon filter-primary"
              src="@/images/modal-close.svg"
              alt=""
            />
          </div>
          <div class="blackcolor pop-800">Vygenerovanie žiadosti</div>
          <button
            class="w-button"
            :disabled="!canSavePdf || savingPdf"
            @click="savePdfToCloud"
          >
            {{ savingPdf ? "Ukladám..." : "Uložiť" }}
          </button>
        </div>
        <div class="information-wrapper">
          <div class="header-wrapper-custom">
            <img :src="payload.application?.bank?.logo_square" alt="" width="50px" height="auto">
            <div class="p-12 bg-gray-100 border border-gray-300" @drop="drop">
              <input
                type="file"
                name="vygenerovana-ziadost"
                id="vygenerovana-ziadost"
                @change="onChange"
                ref="file" accept=".pdf"
              />
              <label
                for="vygenerovana-ziadost"
                class="sub-ctass image-upload-btn add-file-button pointer blc-color pop-800 mb-0 drag-drop-label"
              >
                {{ filelist.length === 0 ? "+vyplnená žiadosť" : filelist[0].name }}
              </label>
            </div>
          </div>
        </div>
        <div v-if="aiGenerated" class="ai-edit-hint">
          <span aria-hidden="true">✓</span>
          <div>
            <strong>Skontrolujte a upravte doplnené údaje</strong>
            <p>
              PDF je editovateľné priamo. Tlačidlo „Uložiť“ uloží aktuálnu verziu
              vrátane všetkých vašich zmien.
            </p>
            <small v-if="aiMissingCount">
              {{ aiMissingCount }} dôležitých údajov ostalo na ručné doplnenie.
            </small>
            <small v-if="aiMappedCount">
              {{ aiMappedCount }} polí bolo vyplnených overeným bankovým mapovaním.
            </small>
            <small v-if="aiReviewRequiredCount">
              {{ aiReviewRequiredCount }} neistých návrhov sa automaticky nezapísalo.
            </small>
          </div>
        </div>
        <div v-else-if="aiError" class="ai-error-hint">
          <span aria-hidden="true">!</span>
          <div>{{ aiError }}</div>
        </div>
        <div class="cloud-holder-wrapper ">
          <mortgage-summary :summary-data="payload.application"/>
        </div>
        <client-section :client-data="applicantData" :role="'Žiadateľ'" :opened-default="opened"/>
        <client-section v-if="coApplicantData.clientObject" :client-data="coApplicantData" :role="'Spolužiadateľ'" :opened-default="opened"/>
        <div
          class="cloud-holder-wrapper"
          v-for="(property, index) in foundedPropertiesData"
          :key="index + 'founded'"
        >
          <property-summary :summary-data="property" :label="`Zabezpečenie úveru ${index+1}`" :opened-default="opened"/>
        </div>
        <div
          class="cloud-holder-wrapper"
          v-for="(property, index) in financedPropertiesData"
          :key="index + 'financed'"
        >
          <property-summary :summary-data="property" :label="`Predmet nadobúdania ${index+1}`" :opened-default="opened"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PropertySummary from "./PropertySummary";
import MortgageSummary from "./MortgageSummary";
import EmploymentSummary from "./EmploymentSummary";
import TradesmanSummary from "./TradesmanSummary";
import DebtSummary from "./DebtSummary";
import SroSummary from "./SroSummary";
import ClientSummary from "./ClientSummary";
import ClientSection from "@/components/shared/PopupHandler/Popups/ApplicationGenerator/ClientSection.vue";
import ApplicationPdfEditor from "@/components/shared/PopupHandler/Popups/ApplicationGenerator/ApplicationPdfEditor.vue";
import propertyFilters from "@/mixins/propertyFilters";
import {
  buildSubmissionOverviewPdf,
  shouldGenerateSubmissionOverview,
} from "@/utils/applicationSubmissionPdf";
import {
  fillApplicationPdf,
  inspectApplicationPdf,
} from "@/utils/applicationAiPdf";
import {
  generateApplicationFieldValues,
  getApplicationTemplatePdf,
} from "@/services/brokerService";

const AI_LOADING_MESSAGES = [
  "Načítavam formulár banky…",
  "Párujem údaje žiadateľa a spolužiadateľa…",
  "Dopĺňam príjmy, záväzky a nehnuteľnosti…",
  "Kontrolujem formát a chýbajúce údaje…",
];

export default {
  name: "ApplicationGeneratorPopUp",
  props: {
    payload: Object,
  },
  components: {
    ApplicationPdfEditor,
    ClientSection,
    ClientSummary,
    SroSummary,
    DebtSummary,
    TradesmanSummary,
    EmploymentSummary,
    MortgageSummary,
    PropertySummary
  },
  mixins: [
    propertyFilters,
  ],
  computed: {
    canSavePdf() {
      return this.filelist.length > 0
        || (this.activeDocumentId === "ai-generated-application" && this.aiEditorReady);
    },
  },
  async created() {
    this.applicantData = this.$store.getters["mortgageDetailState/getFullApplicant"];
    this.coApplicantData = this.$store.getters["mortgageDetailState/getFullCoApplicant"];
    const propertyObjects = this.$store.getters["mortgageDetailState/getProperties"];
    this.financedPropertiesData = this.filterFinancedPropertiesById(propertyObjects);
    this.foundedPropertiesData = this.filterFoundedPropertiesById(propertyObjects);
    const application = this.payload.application;
    const bankCode = application.bank?.code_name;
    const bankDocuments = Array.isArray(application.application_documents)
      ? application.application_documents
          .filter((document) => document.file)
          .map((document) => ({
            id: `bank-document-${document.id}`,
            label: document.label,
            file: document.file,
            isDefault: document.is_default,
            documentType: document.document_type,
          }))
      : [];

    const selectedApplicationTemplate = bankDocuments.find(
      (document) => document.documentType === "application" && document.isDefault,
    ) || bankDocuments.find((document) => document.documentType === "application");
    this.aiTemplateAvailable = Boolean(selectedApplicationTemplate);

    if (bankDocuments.length === 0 && application.ziadost_o_uver) {
      bankDocuments.push({
        id: "legacy-application-template",
        label: "Žiadosť o úver",
        file: application.ziadost_o_uver,
        isDefault: true,
        documentType: "application",
      });
      this.aiTemplateAvailable = true;
    }

    if (bankDocuments.length === 0 && shouldGenerateSubmissionOverview(bankCode)) {
      try {
        const generatedPdf = await buildSubmissionOverviewPdf({
          application,
          applicantData: this.applicantData,
          coApplicantData: this.coApplicantData,
          foundedPropertiesData: this.foundedPropertiesData,
          financedPropertiesData: this.financedPropertiesData,
        });
        this.generatedPdfUrl = URL.createObjectURL(generatedPdf);
        bankDocuments.push({
          id: "generated-submission-overview",
          label: "Podací prehľad",
          file: this.generatedPdfUrl,
          isDefault: true,
        });
      } catch (error) {
        this.$store.commit("appState/setSuccessMessage", {
          text: "Podací prehľad sa nepodarilo vygenerovať.",
          type: "error",
        });
      }
    }

    const applicationPdf = this.$store.getters["mortgageDetailCloud/getCloudFileFromCloudByType"](application.cloud, "vygenerovana_ziadost");
    if (applicationPdf) {
      this.$store.commit("appState/setSuccessMessage", {
        text: "Súbor bol identifikovaný",
        type: "success"
      });
      bankDocuments.unshift({
        id: `uploaded-application-${applicationPdf.id || "current"}`,
        label: "Uložená žiadosť",
        file: applicationPdf.file,
        isDefault: true,
      });
    }

    this.documentTabs = bankDocuments;
    const initialDocument = applicationPdf
      ? bankDocuments[0]
      : bankDocuments.find((document) => document.isDefault) || bankDocuments[0];
    if (initialDocument) this.selectDocumentTab(initialDocument);
    this.loadingPdf = false;
  },
  beforeDestroy() {
    if (this.generatedPdfUrl) URL.revokeObjectURL(this.generatedPdfUrl);
    if (this.aiGeneratedPdfUrl) URL.revokeObjectURL(this.aiGeneratedPdfUrl);
    this.stopAiLoadingMessages();
  },
  methods: {
    selectDocumentTab(document) {
      this.activeDocumentId = document.id;
      this.sourcePdf = document.file;
    },
    startAiLoadingMessages() {
      this.stopAiLoadingMessages();
      let messageIndex = 0;
      this.aiLoadingMessage = AI_LOADING_MESSAGES[messageIndex];
      this.aiLoadingTimer = window.setInterval(() => {
        messageIndex = (messageIndex + 1) % AI_LOADING_MESSAGES.length;
        this.aiLoadingMessage = AI_LOADING_MESSAGES[messageIndex];
      }, 1800);
    },
    stopAiLoadingMessages() {
      if (this.aiLoadingTimer) window.clearInterval(this.aiLoadingTimer);
      this.aiLoadingTimer = null;
    },
    aiErrorMessage(error) {
      const responseData = error?.response?.data;
      if (responseData?.detail) return responseData.detail;
      if (Array.isArray(responseData?.fields)) return responseData.fields[0];
      return error?.message || "Žiadosť sa nepodarilo vyplniť pomocou AI.";
    },
    async generateWithAi() {
      if (this.aiGenerating) return;
      this.aiGenerating = true;
      this.aiError = null;
      this.startAiLoadingMessages();

      try {
        const templateBytes = await getApplicationTemplatePdf(this.payload.application.id);
        const {pdfDoc, fields} = await inspectApplicationPdf(templateBytes);
        if (!fields.length) {
          throw new Error(
            "PDF tejto banky neobsahuje editovateľné polia. Je potrebná novšia interaktívna šablóna.",
          );
        }

        const generatedValues = await generateApplicationFieldValues(
          this.payload.application.id,
          fields,
        );
        const {blob, appliedCount} = await fillApplicationPdf(
          pdfDoc,
          generatedValues.fields,
        );

        if (this.aiGeneratedPdfUrl) URL.revokeObjectURL(this.aiGeneratedPdfUrl);
        this.aiEditorReady = false;
        this.aiEditorDirty = false;
        this.aiEditorVersion += 1;
        this.aiGeneratedPdfBlob = blob;
        this.aiGeneratedPdfUrl = URL.createObjectURL(blob);
        const generatedDocument = {
          id: "ai-generated-application",
          label: "AI návrh žiadosti",
          file: this.aiGeneratedPdfUrl,
          isDefault: true,
          documentType: "application",
        };
        this.documentTabs = [
          generatedDocument,
          ...this.documentTabs.filter((document) => document.id !== generatedDocument.id),
        ];
        this.selectDocumentTab(generatedDocument);
        this.aiGenerated = true;
        this.aiFilledCount = appliedCount;
        this.aiMappedCount = generatedValues.mapped_count || 0;
        this.aiReviewRequiredCount = generatedValues.review_required_count || 0;
        this.aiMissingCount = generatedValues.missing_information?.length || 0;
        this.$store.commit("appState/setSuccessMessage", {
          text: `AI doplnila ${appliedCount} polí. Žiadosť zostáva otvorená na kontrolu a editáciu.`,
          type: "success",
        });
      } catch (error) {
        this.aiError = this.aiErrorMessage(error);
        this.$store.commit("appState/setSuccessMessage", {
          text: this.aiError,
          type: "error",
        });
      } finally {
        this.aiGenerating = false;
        this.stopAiLoadingMessages();
      }
    },
    onAiEditorError(error) {
      this.aiEditorReady = false;
      this.aiError = error?.message || "Žiadosť sa nepodarilo otvoriť na editáciu.";
      this.$store.commit("appState/setSuccessMessage", {
        text: this.aiError,
        type: "error",
      });
    },
    async createFileBlob(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = (event) => {
          const data = event.target.result;
          const blob = new Blob([data], { type: file.type });
          resolve(blob);
        }
        reader.onerror = reject;
      });
    },
    async savePdfToCloud() {
      if (!this.canSavePdf || this.savingPdf) {
        if (!this.canSavePdf) {
          this.$store.commit("appState/setSuccessMessage", {
            text: "Najprv vygenerujte alebo vyberte vyplnenú žiadosť vo formáte PDF.",
            type: "error"
          });
        }
        return;
      }

      this.savingPdf = true;

      try {
        let file = this.filelist[0];
        if (!file) {
          const editedPdf = await this.$refs.aiPdfEditor.exportPdf();
          const bankCode = this.payload.application?.bank?.code_name || "banka";
          file = new File(
            [editedPdf],
            `${bankCode}-ziadost-${this.payload.application.id}.pdf`,
            {type: "application/pdf"},
          );
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", file.name);
        formData.append("document_type", "vygenerovana_ziadost");

        await this.$store.dispatch("mortgageDetailState/replaceApplicationDocument", {
          applicationId: this.payload.application.id,
          formData,
        });

        this.$store.commit("appState/setSuccessMessage", {
          text: "Žiadosť bola uložená a stav bol aktualizovaný.",
          type: "success"
        });
        this.closePopup();
      } catch (error) {
        this.$store.commit("appState/setSuccessMessage", {
          text: "Žiadosť sa nepodarilo uložiť. Skúste to prosím znova.",
          type: "error"
        });
      } finally {
        this.savingPdf = false;
      }
    },
    onChange() {
      this.filelist = this.$refs.file.files;
    },
    remove(i) {
      this.filelist.splice(i, 1);
    },
    getLinkToApplication(bankKey) {
      switch(bankKey) {
        case "mbank": return "https://www.mbankbrokers.cz/login/index";
        case "vub": return "https://ib.vub.sk/eap/login";
        case "slsp": return "https://partnerportal.slsp.sk/partnerportal/sk";
        default: return "";
      }
    },
    dragover(event) {
      event.preventDefault();
      if (!this.sourcePdf || this.classAssigned) return;
      const label = event.currentTarget.getElementsByClassName("drag-drop-label")[0];
      label.textContent = "Žiadosť ako PDF pretiahnite sem";
      this.classAssigned = true;
      label.classList.add("drag-active");
    },
    dragleave(event) {
      if (!this.sourcePdf) return;
      const label = event.currentTarget.getElementsByClassName("drag-drop-label")[0];
      label.textContent = "+vyplnená žiadosť"
      this.classAssigned = false;
      label.classList.remove("drag-active")
    },
    drop(event) {
      event.preventDefault();
      if (!this.sourcePdf) return;
      this.$refs.file.files = event.dataTransfer.files;
      const label = event.currentTarget.getElementsByClassName("drag-drop-label")[0];
      label.textContent = event.dataTransfer.files[0].name;
      label.classList.remove("drag-active")
      this.onChange();
    },
    closePopup () {
      this.$store.commit("popupManagerState/popItemFromPopupOrder");
    },
  },
  data: () => ({
    classAssigned: false,
    savingPdf: false,
    filelist: [], // Store our uploaded files
    sourcePdf: null,
    documentTabs: [],
    activeDocumentId: null,
    generatedPdfUrl: null,
    aiGeneratedPdfUrl: null,
    aiGeneratedPdfBlob: null,
    aiEditorReady: false,
    aiEditorDirty: false,
    aiEditorVersion: 0,
    aiTemplateAvailable: false,
    aiGenerating: false,
    aiGenerated: false,
    aiFilledCount: 0,
    aiMappedCount: 0,
    aiReviewRequiredCount: 0,
    aiMissingCount: 0,
    aiError: null,
    aiLoadingMessage: AI_LOADING_MESSAGES[0],
    aiLoadingTimer: null,
    loadingPdf: true,
    opened: false,
    applicantData: null,
    coApplicantData: null,
    foundedPropertiesData: null,
    financedPropertiesData: null,
  }),
}
</script>

<style lang="scss" scoped>

input[type="file"] {
  display: none;
}

.infomation-section {
  overflow-y: auto;
}

.pdf-section {
  flex-direction: column;
  min-width: 0;
}

.pdf-viewer {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.application-document-tabs {
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
  overflow-x: auto;
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--app-border);
  background: var(--app-surface-muted);
}

.application-document-tab {
  flex: 0 0 auto;
  max-width: 250px;
  overflow: hidden;
  padding: 8px 12px;
  border: 1px solid var(--app-border);
  border-radius: 9px;
  background: var(--app-surface);
  color: var(--app-text-muted);
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.active {
    border-color: #2d8cff;
    background: #e8f3ff;
    color: #1166c2;
  }
}

.ai-generation-bar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 13px 16px;
  border-bottom: 1px solid #cfe0f7;
  background:
    radial-gradient(circle at 10% 0%, rgba(76, 137, 255, 0.18), transparent 42%),
    linear-gradient(110deg, #f5f9ff 0%, #eef5ff 54%, #f8f4ff 100%);

  &.completed {
    border-bottom-color: #c8eadb;
    background:
      radial-gradient(circle at 10% 0%, rgba(44, 172, 124, 0.16), transparent 42%),
      linear-gradient(110deg, #f2fbf7 0%, #effaf8 56%, #f5f9ff 100%);
  }
}

.ai-generation-copy {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;

  > div {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  strong {
    color: #142b4a;
    font-size: 13px;
    font-weight: 800;
  }

  span:not(.ai-sparkle) {
    overflow: hidden;
    color: #5e718d;
    font-size: 11px;
    line-height: 1.4;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.ai-sparkle {
  display: grid;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 1px solid rgba(79, 107, 255, 0.2);
  border-radius: 11px;
  background: linear-gradient(145deg, #526dff, #8e59e8);
  box-shadow: 0 8px 20px rgba(76, 97, 220, 0.24);
  color: #fff;
  font-size: 17px;
}

.ai-generate-button {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 15px;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, #315eea, #7556df);
  box-shadow: 0 8px 18px rgba(58, 84, 194, 0.22);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    box-shadow: 0 10px 24px rgba(58, 84, 194, 0.3);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.76;
  }
}

.ai-button-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.36);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ai-spin 700ms linear infinite;
}

.pdf-canvas {
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
}

#iframepdf {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: 0;
}

.ai-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 28px;
  background: rgba(245, 249, 255, 0.9);
  backdrop-filter: blur(7px);
  color: #1b2f50;
  text-align: center;

  strong {
    margin-top: 18px;
    font-size: 18px;
    font-weight: 800;
  }

  > span:not(.ai-orbit) {
    margin-top: 7px;
    color: #667995;
    font-size: 12px;
  }
}

.ai-orbit {
  position: relative;
  display: grid;
  width: 72px;
  height: 72px;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #3a65ed, #8157df);
  box-shadow: 0 18px 46px rgba(66, 89, 200, 0.3);
  color: #fff;
}

.ai-orbit-ring {
  position: absolute;
  inset: -10px;
  border: 2px solid rgba(74, 99, 222, 0.22);
  border-top-color: #586de5;
  border-radius: 50%;
  animation: ai-spin 1.2s linear infinite;
}

.ai-orbit-sparkle {
  font-size: 28px;
  animation: ai-pulse 1.35s ease-in-out infinite;
}

.ai-loading-dots {
  display: flex;
  gap: 5px;
  margin-top: 16px;

  i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #6075df;
    animation: ai-dot 1.1s ease-in-out infinite;
  }

  i:nth-child(2) { animation-delay: 120ms; }
  i:nth-child(3) { animation-delay: 240ms; }
}

.ai-overlay-enter-active,
.ai-overlay-leave-active {
  transition: opacity 180ms ease;
}

.ai-overlay-enter,
.ai-overlay-leave-to {
  opacity: 0;
}

.ai-edit-hint,
.ai-error-hint {
  display: flex;
  gap: 10px;
  margin: 12px 16px 4px;
  padding: 12px;
  border: 1px solid #bee5d3;
  border-radius: 12px;
  background: #f1fbf6;
  color: #214d3d;

  > span {
    display: grid;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    place-items: center;
    border-radius: 50%;
    background: #2aab79;
    color: #fff;
    font-size: 12px;
    font-weight: 900;
  }

  strong {
    display: block;
    margin-bottom: 3px;
    font-size: 12px;
    font-weight: 800;
  }

  p,
  small {
    margin: 0;
    font-size: 11px;
    line-height: 1.45;
  }

  small {
    display: block;
    margin-top: 5px;
    font-weight: 700;
  }
}

.ai-error-hint {
  border-color: #f0c6c6;
  background: #fff5f5;
  color: #7b3434;
  font-size: 11px;
  line-height: 1.45;

  > span {
    background: #d95454;
  }
}

@keyframes ai-spin {
  to { transform: rotate(360deg); }
}

@keyframes ai-pulse {
  0%, 100% { transform: scale(0.9) rotate(-5deg); opacity: 0.82; }
  50% { transform: scale(1.08) rotate(5deg); opacity: 1; }
}

@keyframes ai-dot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.38; }
  30% { transform: translateY(-5px); opacity: 1; }
}

.popup {
  display: flex;
  flex-direction: row;
}

.infomation-section {
  width: 35%;
  @media screen and (max-width: 767px) {
    width: 100%;
  }
}

.popup {
  width: 90% !important;
  @media screen and (max-width: 767px) {
    width: 90% !important;
  }
}

@media screen and (max-width: 900px) {
  .ai-generation-copy span:not(.ai-sparkle) {
    display: none;
  }
}

@media screen and (max-width: 767px) {
  .ai-generation-bar {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .ai-generate-button {
    width: 100%;
  }
}

</style>
